import React from "react";

import { AlertV4, ChipV4 } from "../common/ui";
import type { ChipV4Tone } from "../common/ui/ChipV4";

import { othersOf, peerSummaryOf, roleLabelOf } from "./presenceFormatV4";
import type { PresencePeer } from "./presenceTypesV4";

/**
 * HIS V4 · แถบ "หน้านี้มีผู้อื่นกำลังใช้งานอยู่"
 *
 * ใช้ component กลางของ V4 เท่านั้น (AlertV4 + ChipV4) ตาม policy ของทีมใน architectureV4.test.ts
 * ไม่ gate การบันทึกใด ๆ ของผู้ใช้ — phase 1 เป็นแจ้งเตือนอย่างเดียว
 */

export type PresenceBarV4Props = {
  /** จาก usePresenceV4() — เฉพาะผู้ใช้คนอื่น */
  peers: PresencePeer[];
  /** ws = อัปเดตผ่าน websocket · poll = REST polling ทุก 10 วินาที (แสดงใน tooltip ตอนทดสอบ/Support) */
  channel?: string;
};

const roleToneOf = (peer: PresencePeer): ChipV4Tone => {
  const role = String(peer.role || "").toUpperCase();
  /* โทนที่มี pair bg/ink ครบใน $hv4-tones */
  if (role.includes("DOCTOR")) return "navy";
  if (role.includes("NURSE")) return "teal";
  if (role.includes("PHARMAC")) return "purple";
  if (role.includes("DENTIST")) return "purple-soft";
  return "neutral";
};

const PresenceBarV4 = (props: PresenceBarV4Props): React.ReactElement | null => {
  const others = othersOf(props.peers || []);
  if (!others.length) return null;

  return (
    <AlertV4 tone="info" className="hv4-presence">
      <span className="hv4-presence-head" title={`ช่องทางอัปเดต: ${props.channel || "-"}`}>
        👥 หน้านี้มีผู้อื่นกำลังใช้งานอยู่
      </span>
      <span className="hv4-presence-list">
        {others.map((peer) => {
          const summary = peerSummaryOf(peer);
          return (
            <span key={peer.sid} className="hv4-presence-peer" title={summary.title}>
              <ChipV4 tone={roleToneOf(peer)} size="sm" className="hv4-presence-name">
                {peer.name || "ไม่ทราบชื่อ"}
                <span className="hv4-presence-role"> · {roleLabelOf(peer.role)}</span>
              </ChipV4>
              <ChipV4 tone={summary.tone} size="sm">
                {summary.text}
              </ChipV4>
            </span>
          );
        })}
      </span>
    </AlertV4>
  );
};

export default PresenceBarV4;
